import {
  Component,
  OnInit,
  OnDestroy,
  NgZone,
  ViewChild,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { ReasonService } from '../../services/reason.service';
import { DocumentsService } from '../../services/documents.service';
import { Global } from '../../../../app.global';
import { filterTouchedQuestions } from '../../helper/touched-questions';
import { GlobalObservables } from '../../../../app.global.observer';
import { ReasonModel } from '../../viewmodels/reason.model';
import { SubscriptionLike as ISubscription } from 'rxjs';
import {
  UploadEvent,
  RemoveEvent,
  FileInfo,
  FileState,
  SuccessEvent,
  ErrorEvent,
  SelectEvent,
  UploadComponent,
} from '@progress/kendo-angular-upload';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { QuestionService } from '../../services/question.service';
import { QuestionAndAnswerModel } from '../../viewmodels/questionAndAnswer.model';
import { SettingsService } from '../../../../authentication/services/settings.service';
import { StorageService } from '../../services/storage.service';
import { Subject } from 'rxjs';
import { takeUntil, finalize } from 'rxjs/operators';

interface UploadedMeta {
  name: string;
  size: number;
  sessionGuid: string;
  pcn: string;
}

interface EvidenceDocumentGuess {
  documentType: string;
  confidence: number | null;
}

interface NexusUploadResponse {
  reasonId?: number[];
  challengeText?: string;
  sessionGuid?: string;
  summary?: string;
  evidence?: string;
  evidenceDocuments?: string[] | null;
  evidenceDocumentGuesses?: EvidenceDocumentGuess[] | null;
  evidenceDocumentConfidences?: EvidenceDocumentGuess[] | null;
  fileName?: string;
  fileSize?: number;
}

interface AIAnswer {
  id: number;
}

interface AIQuestion {
  id: number;
  questionClass: number | string;
  defaultAnswer?: unknown;
  answers?: AIAnswer[];
  recommendedAnswerId?: number | null;
}

interface AIAnsweredQuestionsResponse {
  questions?: AIQuestion[];
}

@Component({
  selector: 'app-nexus-upload',
  templateUrl: './nexus-upload.component.html',
  styleUrl: './nexus-upload.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class NexusUploadComponent implements OnInit, OnDestroy {
  @ViewChild('summarySection') summarySectionRef!: ElementRef;
  @ViewChild('upload') upload!: UploadComponent;

  nexusInputMode: 'dragdrop' | 'freetext' | 'link' = 'dragdrop';
  isFileUploaded = false;
  isTextInputEntered = false;
  isWaitingForSummary = false;
  isSubmitting = false;

  freeTextInput = '';
  linkInput = '';
  summaryText: string | null = null;
  evidenceText: string | null = null;
  evidenceDocuments: string | null = null;
  lockedPdfError: string | null = null;

  autoUploadEnabled = true;
  aiQuestionsEnabled = false;
  spinnerStyle = 'spinner3';

  currentProgressText = 'Summarising your document…';

  uploadSaveUrl = '';
  uploadRemoveUrl = environment.apiPath + 'api/nexus/deleteFile';
  extensionPreviewUrl: string | null = null;
  cancelNexusRequest$ = new Subject<void>();

  public requestHeaders: Array<{ name: string; value: string }> = [];
  public selectedReasons: number[] = [];
  public selectedReasonsFormatted!: ReasonModel;

  public progressPhrases = [
    'uploading document…',
    'reading challenge…',
    'assessing document types…',
    'selecting reasons…',
    'generating AI summary…',
  ];

  persistedFiles: FileInfo[] = [];
  uploadedMeta: UploadedMeta | null = null;
  showUploadedBadge = false;
  uploadedNames: string[] = [];

  private saveSubscription?: ISubscription;
  private progressTimer?: number;
  private currentSessionGuid: string | null = null;
  private batchActive = false;
  private progressStartTime?: number;
  private readonly LONG_WAIT_THRESHOLD_MS = 70000;
  private internalRemove = false;
  private uploadedFilesCount = 0;
  private isResetting = false;
  private lockedPdfTimeout?: number;
  private extensionPayloadInterval?: number;

  private pendingFiles: FileInfo[] = [];

  constructor(
    private reasonService: ReasonService,
    private documentsService: DocumentsService,
    public global: Global,
    public globalObs: GlobalObservables,
    public questionServ: QuestionService,
    private settingsService: SettingsService,
    private storageService: StorageService,
    private http: HttpClient,
    private ngZone: NgZone
  ) {}

  get isUploading(): boolean {
    return this.batchActive || this.isSubmitting;
  }

  get progressChars(): string[] {
    return (this.currentProgressText || '').split('');
  }

  get canUpload(): boolean {
    return this.pendingFiles.length > 0 && !this.isUploading;
  }

  public get uploadedDisplayName(): string {
    if (this.uploadedNames && this.uploadedNames.length) {
      return Array.from(new Set(this.uploadedNames)).filter(Boolean).join(', ');
    }
    return this.uploadedMeta?.name ?? '';
  }

  private get LS_KEYS() {
    const pcn = this.global?.PCN?.pcn ?? 'unknown';
    return {
      mode: 'nexus_input_mode',
      freeText: `nexus_free_text_${pcn}`,
      link: `nexus_link_${pcn}`,
      summary: `nexus_summary_${pcn}`,
      evidence: `nexus_evidence_${pcn}`,
      evidenceDocs: `evidenceDocuments_${pcn}`,
      evidenceDocGuesses: `evidenceDocumentGuesses_${pcn}`,
      evidenceDocConfidences: `evidenceDocumentConfidences_${pcn}`,
      sessionGuid: `sessionGuid_${pcn}`,
    };
  }

  showReset(): boolean {
    return this.isUploading || !!(this.summaryText && this.summaryText.trim());
  }

  ngOnInit(): void {
    this.ensureSession();
    window.addEventListener('rm-extension-pdf-ready', this.extensionPdfListener);

    this.waitForExtensionPayload();

    const savedMode = localStorage.getItem(this.LS_KEYS.mode) as
      | 'dragdrop'
      | 'freetext'
      | 'link'
      | null;
    if (savedMode && ['dragdrop', 'freetext', 'link'].includes(savedMode)) {
      this.nexusInputMode = savedMode;
    }
    this.freeTextInput = localStorage.getItem(this.LS_KEYS.freeText) ?? '';
    this.linkInput = localStorage.getItem(this.LS_KEYS.link) ?? '';
    this.summaryText = localStorage.getItem(this.LS_KEYS.summary);
    this.evidenceText = localStorage.getItem(this.LS_KEYS.evidence);
    this.evidenceDocuments = localStorage.getItem(this.LS_KEYS.evidenceDocs);

    const metaRaw = localStorage.getItem(this.getMetaKey());
    if (metaRaw) {
      try {
        const meta = JSON.parse(metaRaw) as UploadedMeta;
        this.uploadedMeta = meta;
        this.showUploadedBadge = true;
        this.isFileUploaded = true;
        this.persistedFiles = [
          {
            name: meta.name,
            size: meta.size ?? 0,
            extension: '.' + (meta.name.split('.').pop() || '').toLowerCase(),
            state: FileState.Uploaded,
            uid: this.makeUid(),
          },
        ];
        this.uploadedFilesCount = this.persistedFiles.length;
      } catch {
        this.uploadedFilesCount = 0;
      }
    } else {
      this.uploadedFilesCount = 0;
    }

    const savedNamesRaw = localStorage.getItem(this.getNamesKey());
    if (savedNamesRaw) {
      try {
        this.uploadedNames = JSON.parse(savedNamesRaw) as string[];
      } catch {
        this.uploadedNames = [];
      }
    }
    if (!this.uploadedNames.length) {
      const fromPersisted = (this.persistedFiles || [])
        .filter((f) => f.state === FileState.Uploaded)
        .map((f) => f.name);
      this.uploadedNames = fromPersisted.length
        ? fromPersisted
        : this.uploadedMeta?.name
          ? [this.uploadedMeta.name]
          : [];
    }
    localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));
    this.refreshUploadedMetaName();

    const userData = localStorage.getItem('rm_current_user');
    const userId: number | null = userData ? JSON.parse(userData).userId : null;
    const username: string = userData ? JSON.parse(userData).username : '';
    const pcn = this.global?.PCN?.pcn ?? '';
    const contravention = this.global?.PCN?.contravention ?? '';

    this.uploadSaveUrl =
      environment.apiPath +
      'api/nexus/uploadFile?pcnNumber=' +
      encodeURIComponent(pcn) +
      '&userId=' +
      encodeURIComponent(userId ?? '') +
      '&username=' +
      encodeURIComponent(username) +
      '&contravention=' +
      encodeURIComponent(contravention);

    this.saveSubscription = this.globalObs.onLetterSaved.subscribe(() => this.handleSave());

    this.settingsService
      .getApplicationSetting('NexusAnswerQuestions')
      .subscribe((result: string) => {
        this.aiQuestionsEnabled = typeof result === 'string' && result.toLowerCase() === 'true';
      });
    this.settingsService.getApplicationSetting('Nexus.AutoUploadEnabled').subscribe({
      next: (result: string) =>
        (this.autoUploadEnabled =
          typeof result === 'string' ? result.toLowerCase() === 'true' : true),
      error: () => (this.autoUploadEnabled = true),
    });
    this.settingsService.getApplicationSetting('Council.SpinnerStyle').subscribe({
      next: (result: string) =>
        (this.spinnerStyle = typeof result === 'string' && result ? result : 'spinner3'),
      error: () => (this.spinnerStyle = 'spinner3'),
    });

    this.loadInputModeSetting();
  }

  ngOnDestroy(): void {
    this.saveSubscription?.unsubscribe();
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }
    if (this.extensionPayloadInterval) {
      clearInterval(this.extensionPayloadInterval);
      this.extensionPayloadInterval = undefined;
    }
    window.removeEventListener('rm-extension-pdf-ready', this.extensionPdfListener);
    if (this.extensionPreviewUrl) {
      URL.revokeObjectURL(this.extensionPreviewUrl);
    }
  }

  waitForExtensionPayload(): void {
    console.log('[RM] checking for extension payload');
    let attempts = 0;
    const maxAttempts = 20;

    this.extensionPayloadInterval = window.setInterval(() => {
      attempts++;

      const payload = (window as any).__RM_EXTENSION_UPLOAD__;

      if (payload?.base64) {
        console.log('[RM] payload detected');
        clearInterval(this.extensionPayloadInterval);
        this.extensionPayloadInterval = undefined;
        this.extensionPdfListener();
        return;
      }

      if (attempts >= maxAttempts) {
        clearInterval(this.extensionPayloadInterval);
        this.extensionPayloadInterval = undefined;
      }
    }, 1000);
  }
  persistFreeText(val: string) {
    localStorage.setItem(this.LS_KEYS.freeText, val ?? '');
  }

  persistLink(val: string) {
    localStorage.setItem(this.LS_KEYS.link, val ?? '');
  }

  splitSummaryReasons(): string[] {
    const raw = this.summaryText || '';
    return raw
      .split(':;')
      .map((i) => i.trim())
      .filter(Boolean);
  }

  splitEvidenceItems(): string[] {
    const raw = this.evidenceText || '';
    return raw
      .split(' - ')
      .map((i) => i.trim())
      .filter(Boolean);
  }

  loadInputModeSetting(): void {
    this.settingsService.getApplicationSetting('NexusDragDropDefault').subscribe({
      next: (value: string) => {
        if (this.linkInput?.trim()) {
          this.nexusInputMode = 'link';
        } else {
          const lowerVal = value?.toLowerCase();
          this.nexusInputMode =
            lowerVal === 'freetext' ? 'freetext' : lowerVal === 'link' ? 'link' : 'dragdrop';
        }
        localStorage.setItem(this.LS_KEYS.mode, this.nexusInputMode);
      },
      error: () => {
        if (!this.linkInput?.trim()) {
          this.nexusInputMode = 'dragdrop';
        }
      },
    });
  }

  async handleLinkSubmit(): Promise<void> {
    const link = this.linkInput.trim();
    if (!link) {
      return;
    }

    localStorage.setItem(this.LS_KEYS.link, link);

    this.summaryText = '';
    this.isWaitingForSummary = true;
    this.isSubmitting = true;
    this.isTextInputEntered = true;
    this.startFakeProgress();

    try {
      const rmAccessToken = localStorage.getItem('rm_access_token') || '';
      const userData = localStorage.getItem('rm_current_user');
      const userId: number | null = userData ? JSON.parse(userData).userId : null;
      const username: string | null = userData ? JSON.parse(userData).username : null;
      const pcnNumber = this.global.PCN.pcn;
      const contravention = this.global.PCN.contravention;
      const guessedFileName = link.split('/').pop() || `upload-${Date.now()}.pdf`;

      const pdfResponse = await fetch(link, { method: 'GET' });
      if (!pdfResponse.ok) {
        throw new Error(`Failed to download PDF from link (${pdfResponse.status}).`);
      }
      const pdfBlob = await pdfResponse.blob();

      if (await this.isPdfBlob(pdfBlob)) {
        const file = new File([pdfBlob], guessedFileName, { type: 'application/pdf' });
        const form = new FormData();

        form.append('files', file, file.name);
        form.append('sessionGuid', this.ensureSession());

        let headers = new HttpHeaders();
        if (rmAccessToken) {
          headers = headers.set('Authorization', `Bearer ${rmAccessToken}`);
        }

        const uploadUrl =
          `${environment.apiPath}api/nexus/uploadFile` +
          `?pcnNumber=${encodeURIComponent(pcnNumber)}` +
          `&userId=${encodeURIComponent(userId ?? '')}` +
          `&username=${encodeURIComponent(username ?? '')}` +
          `&contravention=${encodeURIComponent(contravention)}`;

        const res = (await this.http
          .post<NexusUploadResponse>(uploadUrl, form, { headers })
          .pipe(takeUntil(this.cancelNexusRequest$))
          .toPromise()) as NexusUploadResponse;

        const reasonIds = res?.reasonId ?? [];
        const challengeText = res?.challengeText ?? '';
        const sessionId = res?.sessionGuid ?? '';

        this.summaryText = res?.summary || 'No summary available.';
        this.evidenceText = res?.evidence || 'No evidence summary available.';
        this.evidenceDocuments = (res?.evidenceDocuments as unknown as string) ?? null;
        localStorage.setItem(
          this.LS_KEYS.evidenceDocGuesses,
          JSON.stringify(res?.evidenceDocumentGuesses ?? [])
        );
        localStorage.setItem(
          this.LS_KEYS.evidenceDocConfidences,
          JSON.stringify(res?.evidenceDocumentConfidences ?? [])
        );

        localStorage.setItem(this.LS_KEYS.summary, this.summaryText ?? '');
        localStorage.setItem(this.LS_KEYS.evidence, this.evidenceText ?? '');
        localStorage.setItem(
          this.LS_KEYS.evidenceDocs,
          JSON.stringify(this.evidenceDocuments || [])
        );
        localStorage.setItem(this.LS_KEYS.sessionGuid, sessionId);

        const meta: UploadedMeta = {
          name: res?.fileName || file.name,
          size: res?.fileSize || file.size,
          sessionGuid: sessionId,
          pcn: pcnNumber,
        };
        localStorage.setItem(this.getMetaKey(), JSON.stringify(meta));

        this.isFileUploaded = true;
        this.linkInput = '';
        this.isTextInputEntered = false;

        this.uploadedFilesCount = 1;

        this.uploadedNames = [meta.name];
        localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));
        this.uploadedMeta = meta;
        this.refreshUploadedMetaName();

        this.stopFakeProgress();
        this.isWaitingForSummary = false;

        this.handleReasonAndFetchQuestions(reasonIds, challengeText, sessionId);
      } else {
        this.summaryText = 'Not a pdf file';
        this.stopFakeProgress();
        this.isWaitingForSummary = false;
        this.isSubmitting = false;
        this.isTextInputEntered = false;
      }
    } catch {
      this.summaryText = 'Failed - Please ensure the file is a PDF.';
      this.stopFakeProgress();
      this.isWaitingForSummary = false;
      this.isSubmitting = false;
      this.isTextInputEntered = false;
    }
  }

  extensionPdfListener = () => {
    this.ngZone.run(() => {
      const payload = (window as any).__RM_EXTENSION_UPLOAD__;
      console.log('[RM] extension payload', payload);

      if (!payload?.base64) {
        console.warn('[RM Angular] No base64 on payload', payload);
        return;
      }

      try {
        const fileName = payload.fileName || 'document.pdf';
        const file = this.base64ToPdfFile(payload.base64, fileName);
        this.extensionPreviewUrl = URL.createObjectURL(file);

        delete (window as any).__RM_EXTENSION_UPLOAD__;
        this.handleExtensionFileUpload(file);
      } catch (err) {
        delete (window as any).__RM_EXTENSION_UPLOAD__;
        console.error('Failed to process extension PDF payload', err);
      }
    });
  };
  base64ToPdfFile(base64: string, fileName: string): File {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);

    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }

    return new File([bytes], fileName, { type: 'application/pdf' });
  }

  handleExtensionFileUpload(file: File): void {
    console.log('[RM] uploading extension PDF', {
      name: file.name,
      size: file.size,
    });
    const token = localStorage.getItem('rm_access_token') || '';
    const userData = localStorage.getItem('rm_current_user');
    const userId: number | null = userData ? JSON.parse(userData).userId : null;
    const username: string | null = userData ? JSON.parse(userData).username : null;
    const pcnNumber = this.global.PCN.pcn;
    const contravention = this.global.PCN.contravention;

    const form = new FormData();
    form.append('files', file, file.name);
    form.append('sessionGuid', this.ensureSession());

    let headers = new HttpHeaders().set('Accept', 'application/json');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    const uploadUrl =
      `${environment.apiPath}api/nexus/uploadFile` +
      `?pcnNumber=${encodeURIComponent(pcnNumber)}` +
      `&userId=${encodeURIComponent(userId ?? '')}` +
      `&username=${encodeURIComponent(username ?? '')}` +
      `&contravention=${encodeURIComponent(contravention)}`;

    this.batchActive = true;
    this.isSubmitting = true;
    this.summaryText = null;
    this.isWaitingForSummary = true;
    this.startFakeProgress();
    this.storageService.lockLetterTop(true);

    this.http
      .post<NexusUploadResponse>(uploadUrl, form, { headers })
      .pipe(takeUntil(this.cancelNexusRequest$))
      .subscribe({
        next: (response) => {
          const reasonIds = response?.reasonId ?? [];
          const challengeText = response?.challengeText ?? '';
          const sessionId = response?.sessionGuid ?? '';

          this.summaryText = response?.summary || 'No summary available.';
          this.evidenceText = response?.evidence || 'No evidence summary available.';
          this.evidenceDocuments = (response?.evidenceDocuments as unknown as string) ?? null;
          localStorage.setItem(
            this.LS_KEYS.evidenceDocGuesses,
            JSON.stringify(response?.evidenceDocumentGuesses ?? [])
          );
          localStorage.setItem(
            this.LS_KEYS.evidenceDocConfidences,
            JSON.stringify(response?.evidenceDocumentConfidences ?? [])
          );

          localStorage.setItem(this.LS_KEYS.summary, this.summaryText ?? '');
          localStorage.setItem(this.LS_KEYS.evidence, this.evidenceText ?? '');
          localStorage.setItem(
            this.LS_KEYS.evidenceDocs,
            JSON.stringify(this.evidenceDocuments || [])
          );
          localStorage.setItem(this.LS_KEYS.sessionGuid, sessionId);

          const meta: UploadedMeta = {
            name: response?.fileName || file.name,
            size: response?.fileSize || file.size,
            sessionGuid: sessionId,
            pcn: this.global?.PCN?.pcn ?? 'unknown',
          };

          localStorage.setItem(this.getMetaKey(), JSON.stringify(meta));
          this.uploadedMeta = meta;
          this.isFileUploaded = true;
          this.showUploadedBadge = true;

          this.uploadedNames = [meta.name];
          localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));
          this.refreshUploadedMetaName();

          this.persistedFiles = [
            {
              name: meta.name,
              size: meta.size ?? 0,
              extension: '.' + (meta.name.split('.').pop() || '').toLowerCase(),
              state: FileState.Uploaded,
              uid: this.makeUid(),
            },
          ];

          this.uploadedFilesCount = 1;

          this.storageService.lockLetterTop(false);
          this.isWaitingForSummary = false;
          this.stopFakeProgress();
          this.batchActive = false;
          this.isSubmitting = false;

          this.handleReasonAndFetchQuestions(reasonIds, challengeText, sessionId);
        },
        error: (err) => {
          console.error('[RM Angular] Extension PDF upload failed', err);
          this.stopFakeProgress();
          this.isWaitingForSummary = false;
          this.isSubmitting = false;
          this.batchActive = false;
          this.storageService.lockLetterTop(false);
        },
      });
  }
  uploadEventHandler(e: UploadEvent): void {
    const token = localStorage.getItem('rm_access_token');
    if (token) {
      e.headers = (e.headers || new HttpHeaders())
        .set('Authorization', `Bearer ${token}`)
        .set('Accept', 'application/json');
    }

    e.data = {
      pcnNumber: this.global.PCN.pcn,
      userId: JSON.parse(localStorage.getItem('rm_current_user') || '{}')?.userId,
      username: JSON.parse(localStorage.getItem('rm_current_user') || '{}')?.username,
      contravention: this.global.PCN.contravention,
      sessionGuid: this.ensureSession(),
    };

    if (!this.batchActive) {
      this.batchActive = true;
      this.isSubmitting = true;
      this.summaryText = null;
      this.isWaitingForSummary = true;
      this.startFakeProgress();
      this.storageService.lockLetterTop(true);
    }
  }

  ensureSession(): string {
    if (!this.currentSessionGuid) {
      this.currentSessionGuid =
        localStorage.getItem(this.LS_KEYS.sessionGuid) || crypto.randomUUID();
      localStorage.setItem(this.LS_KEYS.sessionGuid, this.currentSessionGuid);
    }
    return this.currentSessionGuid;
  }

  onUploadSuccess(event: SuccessEvent): void {
    const response = event.response?.body as NexusUploadResponse | undefined;
    const reasonIds = response?.reasonId ?? [];
    const challengeText = response?.challengeText ?? '';
    const sessionId = response?.sessionGuid ?? '';

    this.summaryText = response?.summary || 'No summary available.';
    this.evidenceText = response?.evidence || 'No evidence summary available.';
    this.evidenceDocuments = (response?.evidenceDocuments as unknown as string) ?? null;
    localStorage.setItem(
      this.LS_KEYS.evidenceDocGuesses,
      JSON.stringify(response?.evidenceDocumentGuesses ?? [])
    );
    localStorage.setItem(
      this.LS_KEYS.evidenceDocConfidences,
      JSON.stringify(response?.evidenceDocumentConfidences ?? [])
    );

    localStorage.setItem(this.LS_KEYS.summary, this.summaryText ?? '');
    localStorage.setItem(this.LS_KEYS.evidence, this.evidenceText ?? '');
    localStorage.setItem(this.LS_KEYS.evidenceDocs, JSON.stringify(this.evidenceDocuments || []));
    localStorage.setItem(this.LS_KEYS.sessionGuid, sessionId);

    const firstFile = (event.files && event.files[0]) || null;
    const pcnNumber = this.global?.PCN?.pcn ?? 'unknown';
    const meta: UploadedMeta = {
      name: response?.fileName || firstFile?.name || 'uploaded.pdf',
      size: response?.fileSize || firstFile?.size || 0,
      sessionGuid: sessionId,
      pcn: pcnNumber,
    };
    localStorage.setItem(this.getMetaKey(), JSON.stringify(meta));
    this.isFileUploaded = true;

    this.uploadedFilesCount += event.files?.length ?? 1;

    const namesFromEvent = (event.files || []).map((f) => f.name).filter(Boolean);
    this.uploadedNames = Array.from(new Set([...(this.uploadedNames || []), ...namesFromEvent]));
    localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));

    this.uploadedMeta = meta;
    this.refreshUploadedMetaName();

    this.storageService.lockLetterTop(false);
    this.isWaitingForSummary = false;
    this.stopFakeProgress();

    this.handleReasonAndFetchQuestions(reasonIds, challengeText, sessionId);
  }

  clearExtensionState(): void {
    localStorage.removeItem('payloadPdfUrl');
    delete (window as any).__RM_EXTENSION_UPLOAD__;
    this.currentSessionGuid = null;
  }

  onUploadError(_event: ErrorEvent): void {
    this.stopFakeProgress();
    this.isWaitingForSummary = false;
    this.isSubmitting = false;
    this.batchActive = false;
  }

  onRemove(e: RemoveEvent): void {
    const token = localStorage.getItem('rm_access_token') || '';
    if (token) {
      e.headers = (e.headers || new HttpHeaders()).set('Authorization', `Bearer ${token}`);
    }

    // Internal removal (locked PDF cleanup)
    if (this.internalRemove) {
      this.internalRemove = false;
      return;
    }

    const removingNames = (e.files || []).map((f) => f.name);

    // Was ANY of the removed files actually uploaded before?
    const removingPersisted = (e.files || []).some(
      (f) => f.state === FileState.Uploaded || this.persistedFiles.some((p) => p.name === f.name)
    );

    // Remove from persisted files
    this.persistedFiles = this.persistedFiles.filter((f) => !removingNames.includes(f.name));

    // Remove from uploaded name list
    this.uploadedNames = this.uploadedNames.filter((n) => !removingNames.includes(n));
    localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));

    // Remove from pending list
    this.pendingFiles = this.pendingFiles.filter((f) => !removingNames.includes(f.name));

    this.refreshUploadedMetaName();

    const anyRemaining = this.persistedFiles.length > 0 || this.pendingFiles.length > 0;

    if (!anyRemaining) {
      this.clearUploadedFiles();
      return;
    }

    if (!this.autoUploadEnabled && removingPersisted) {
      this.selectedReasons = [168];
      this.summaryText = null;
      this.evidenceText = null;
      this.evidenceDocuments = null;

      localStorage.removeItem(this.LS_KEYS.summary);
      localStorage.removeItem(this.LS_KEYS.evidence);
      localStorage.removeItem(this.LS_KEYS.evidenceDocs);
      localStorage.removeItem(this.LS_KEYS.evidenceDocGuesses);
      localStorage.removeItem(this.LS_KEYS.evidenceDocConfidences);

      this.updateSelectedReasons()
        .then(() => {
          this.uploadAll();
        })
        .catch(() => {});
    }
  }

  onComplete(): void {
    this.stopFakeProgress();
    this.isWaitingForSummary = false;
    this.isSubmitting = false;
    this.batchActive = false;
    this.storageService.lockLetterTop(false);
  }

  clearUploadedFiles(): void {
    if (this.isResetting) {
      return;
    }

    this.isResetting = true;
    this.selectedReasons = [168];

    this.updateSelectedReasons()
      .catch((err) => {
        console.error('Failed to update reasons to [168] on reset:', err);
      })
      .finally(() => {
        localStorage.removeItem(this.LS_KEYS.sessionGuid);
        localStorage.removeItem(this.LS_KEYS.summary);
        localStorage.removeItem(this.LS_KEYS.evidence);
        localStorage.removeItem(this.LS_KEYS.freeText);
        localStorage.removeItem(this.LS_KEYS.link);
        localStorage.removeItem(this.LS_KEYS.evidenceDocs);
        localStorage.removeItem(this.LS_KEYS.evidenceDocGuesses);
        localStorage.removeItem(this.LS_KEYS.evidenceDocConfidences);
        localStorage.removeItem(this.getMetaKey());
        localStorage.removeItem(this.getNamesKey());

        this.cancelNexusRequest$.next();
        this.freeTextInput = '';
        this.linkInput = '';
        this.summaryText = null;
        this.evidenceText = null;
        this.evidenceDocuments = null;

        this.persistedFiles = [];
        this.uploadedNames = [];
        this.uploadedMeta = null;
        this.isFileUploaded = false;
        this.uploadedFilesCount = 0;
        this.isWaitingForSummary = false;
        this.isSubmitting = false;
        this.batchActive = false;
        this.isTextInputEntered = false;

        this.pendingFiles = [];
        this.isResetting = false;
        this.clearExtensionState();
        window.location.reload();
      });
  }

  isPdfBlob(blob: Blob): Promise<boolean> {
    return new Promise((resolve) => {
      const fr = new FileReader();
      fr.onerror = () => resolve(false);
      fr.onload = () => {
        const bytes = new Uint8Array(fr.result as ArrayBuffer);
        const ok =
          bytes.length >= 5 &&
          bytes[0] === 0x25 &&
          bytes[1] === 0x50 &&
          bytes[2] === 0x44 &&
          bytes[3] === 0x46 &&
          bytes[4] === 0x2d;
        resolve(ok);
      };
      fr.readAsArrayBuffer(blob.slice(0, 5));
    });
  }

  handleTextSubmit(textarea: HTMLTextAreaElement): void {
    const text = textarea.value;
    localStorage.setItem(this.LS_KEYS.freeText, text ?? '');

    this.isSubmitting = true;
    this.isWaitingForSummary = true;
    this.startFakeProgress();

    if (text && text.trim().length > 0) {
      this.isTextInputEntered = true;
      setTimeout(() => this.autoResize(textarea), 1000);
      this.onTextInput(text);
    }
  }

  startFakeProgress(): void {
    let i = 0;
    this.currentProgressText = this.progressPhrases[i];

    this.progressStartTime = Date.now();

    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }

    this.progressTimer = window.setInterval(() => {
      const elapsed = Date.now() - (this.progressStartTime || 0);

      if (i < this.progressPhrases.length - 1) {
        i++;
      }

      let nextText = this.progressPhrases[Math.min(i, this.progressPhrases.length - 1)];

      if (elapsed >= this.LONG_WAIT_THRESHOLD_MS) {
        nextText = 'processing is taking longer than usual - you may want to reset and try again.';
      }

      this.ngZone.run(() => {
        this.currentProgressText = nextText;
      });
    }, 7000);
  }

  stopFakeProgress(): void {
    if (this.progressTimer) {
      clearInterval(this.progressTimer);
    }
    this.progressTimer = undefined as unknown as number;
    this.progressStartTime = undefined;
  }

  onTextInput(text: string): void {
    if (!text) {
      this.isTextInputEntered = false;
      return;
    }

    this.isTextInputEntered = true;
    this.isFileUploaded = false;

    const rmAccessToken = localStorage.getItem('rm_access_token') || '';
    const userData = localStorage.getItem('rm_current_user');
    const userId: number | null = userData ? JSON.parse(userData).userId : null;
    const username: string | null = userData ? JSON.parse(userData).username : null;
    const contravention = this.global.PCN.contravention;
    const pcnNumber = this.global.PCN.pcn;

    let baseUrl = this.uploadSaveUrl.replace('uploadFile', 'uploadText');
    if (baseUrl.includes('?')) {
      baseUrl = baseUrl.split('?')[0];
    }

    const url =
      `${baseUrl}?pcnNumber=${encodeURIComponent(pcnNumber)}` +
      `&sessionGuid=${encodeURIComponent(this.ensureSession())}`;

    const headers = new HttpHeaders({
      Authorization: `Bearer ${rmAccessToken}`,
      'Content-Type': 'application/json',
    });

    const body = { text, userId, username, contravention };

    this.http
      .post(url, body, { headers })
      .pipe(takeUntil(this.cancelNexusRequest$))
      .subscribe({
        next: (res: unknown) => {
          const r = res as NexusUploadResponse;
          const reasonIds = r?.reasonId ?? [];
          const challengeText = r?.challengeText || '';
          const sessionId = r?.sessionGuid || '';

          this.summaryText = r?.summary || 'No summary available.';
          this.evidenceText = r?.evidence || 'No evidence summary available.';
          this.evidenceDocuments = (r?.evidenceDocuments as unknown as string) ?? null;
          localStorage.setItem(
            this.LS_KEYS.evidenceDocConfidences,
            JSON.stringify(r?.evidenceDocumentConfidences ?? [])
          );
          localStorage.setItem(
            this.LS_KEYS.evidenceDocGuesses,
            JSON.stringify(r?.evidenceDocumentGuesses ?? [])
          );

          localStorage.setItem(this.LS_KEYS.summary, this.summaryText ?? '');
          localStorage.setItem(this.LS_KEYS.evidence, this.evidenceText ?? '');
          localStorage.setItem(
            this.LS_KEYS.evidenceDocs,
            JSON.stringify(this.evidenceDocuments || [])
          );
          localStorage.setItem(this.LS_KEYS.sessionGuid, sessionId);

          this.isTextInputEntered = false;
          this.stopFakeProgress();
          this.isWaitingForSummary = false;

          this.handleReasonAndFetchQuestions(reasonIds, challengeText, sessionId);
        },
        error: () => {
          this.stopFakeProgress();
          this.isWaitingForSummary = false;
          this.isSubmitting = false;
        },
      });
  }

  updateSelectedReasons(): Promise<void> {
    return new Promise((resolve, reject) => {
      this.reasonService.updateReasons(this.global.PCN.pcn, this.selectedReasons).subscribe({
        next: () => {
          this.reasonService.getSelectedReasons(this.global.PCN.pcn).subscribe({
            next: (selectedReasons: number[]) => {
              this.selectedReasons = [...selectedReasons];
              resolve();
            },
            error: (error) => reject(error),
          });
        },
        error: (err) => reject(err),
      });
    });
  }

  handleSave(): void {
    const sessionGuid = localStorage.getItem(this.LS_KEYS.sessionGuid);
    const rmAccessToken = localStorage.getItem('rm_access_token');
    if (!sessionGuid || !rmAccessToken) {
      return;
    }

    const headers = new HttpHeaders({
      Authorization: `Bearer ${rmAccessToken}`,
      'Content-Type': 'application/json',
    });

    this.documentsService.getSelectedDocuments(this.global.PCN.pcn).subscribe({
      next: (selectedDocuments) => this.postSessionAudit(sessionGuid, headers, selectedDocuments),
      error: () => this.postSessionAudit(sessionGuid, headers, []),
    });
  }

  autoResize(textarea: HTMLTextAreaElement): void {
    textarea.rows = 5;
    const lineHeight = 24;
    const maxRows = 10;
    textarea.style.height = 'auto';
    const newHeight = textarea.scrollHeight;
    const newRows = Math.min(Math.floor(newHeight / lineHeight), maxRows);
    textarea.rows = newRows;
  }

  handleReasonAndFetchQuestions(
    reasonId: number[],
    challengeText: string,
    sessionId: string
  ): void {
    this.selectedReasons = [168, ...(reasonId || [])];
    this.resetReasons();

    this.updateSelectedReasons()
      .then(() => {
        this.global.pcnBlockService.setLocalPcn(this.global.PCN);
        this.global.PCN = this.global.PCN;
        this.isSubmitting = false;
        this.isWaitingForSummary = false;
        this.stopFakeProgress();

        if (!this.aiQuestionsEnabled) {
          this.globalObs.OnSelectedReasonsUpdated.next(this.selectedReasons);
          this.globalObs.onPcnChanged.next(this.global.PCN);
          this.isSubmitting = false;
          return;
        }

        const pcn = this.global.PCN.pcn;
        const url = `${environment.apiPath}api/nexus/getAIAnsweredQuestions/${pcn}`;

        const body = {
          challengeText,
          sessionGuid: sessionId,
          questionProcess: {
            questionsAndAnswers: this.global.questionsAndAnswers,
            additionalExplanationIds: [] as number[],
          },
        };

        const rmAccessToken = localStorage.getItem('rm_access_token') || '';
        const headers = new HttpHeaders({ Authorization: `Bearer ${rmAccessToken}` });

        this.http.post(url, body, { headers }).subscribe({
          next: (res: unknown) => {
            const r = res as AIAnsweredQuestionsResponse;
            let questions: AIQuestion[] = r?.questions ?? [];

            questions = questions
              .filter((q) => Number(q.questionClass) !== 2)
              .map((q) => {
                const qc = Number(q.questionClass);
                let normalizedDefault: unknown = q.defaultAnswer;

                if (qc === 1 || qc === 3) {
                  // YesNo/MultipleChoice answers are scalar answer IDs, never arrays - keep the
                  // server's value and only fall back to the first option if it's genuinely absent.
                  const first = q.answers && q.answers[0] ? q.answers[0].id : null;
                  normalizedDefault = q.defaultAnswer ?? first;
                } else if (qc === 4) {
                  normalizedDefault = typeof q.defaultAnswer === 'string' ? q.defaultAnswer : '';
                } else {
                  const first = q.answers && q.answers[0] ? q.answers[0].id : null;
                  normalizedDefault = q.defaultAnswer ?? first;
                }

                return {
                  ...q,
                  questionClass: qc,
                  defaultAnswer: normalizedDefault,
                };
              });

            this.global.questionsAndAnswers = this.getQuestionsAndAnswersModel(
              questions.map((q) => ({
                id: q.id,
                questionClass: q.questionClass,
                defaultAnswer: q.defaultAnswer,
                answerSelected: (q as unknown as { answerSelected?: unknown }).answerSelected,
                answers: q.answers,
                recommendedAnswerId: q.recommendedAnswerId ?? null,
              }))
            );
            this.questionServ.setLocalQuestionsAndAnswers(this.global.questionsAndAnswers);

            this.globalObs.onQuestionsChanged.next(true);
            this.globalObs.OnSelectedReasonsUpdated.next(this.selectedReasons);
            this.globalObs.onPcnChanged.next(this.global.PCN);
            this.isSubmitting = false;
          },
          error: () => {},
        });
      })
      .catch(() => {});
  }

  resetReasons(): void {
    this.globalObs.OnSelectedReasonsUpdated.next([]);
    this.reasonService.setLocalReasons([]);
  }

  getQuestionsAndAnswersModel(
    questionsList: Array<{
      id: number;
      questionClass: number | string;
      defaultAnswer?: unknown;
      answerSelected?: unknown;
      answers?: AIAnswer[];
      recommendedAnswerId?: number | null;
    }>
  ): QuestionAndAnswerModel[] {
    return questionsList.map((question) => ({
      questionId: question.id,
      recommendedAnswerId: (question.recommendedAnswerId ?? null) as unknown as number,
      answer: {
        questionClass: Number(question.questionClass),
        value: question.answerSelected ?? question.defaultAnswer,
      },
    }));
  }

  onSelect(e: SelectEvent): void {
    const files = e.files || [];

    files.forEach((fileInfo) => {
      const extension = (fileInfo.extension || '').toLowerCase();

      if (extension === '.pdf' && fileInfo.rawFile) {
        const raw = fileInfo.rawFile as File;

        this.isPdfLocked(raw).then((locked) => {
          if (locked) {
            this.lockedPdfError =
              'the uploaded pdf appeared to be password-protected and has been removed. please upload an unlocked copy.';

            if ((this as any).lockedPdfTimeout) {
              clearTimeout((this as any).lockedPdfTimeout);
            }

            (this as any).lockedPdfTimeout = window.setTimeout(() => {
              this.lockedPdfError = null;
            }, 5000);

            if (this.upload && fileInfo.uid) {
              this.internalRemove = true;
              this.upload.removeFilesByUid(fileInfo.uid);
            }

            return;
          }

          this.lockedPdfError = null;

          this.pendingFiles = [...this.pendingFiles, fileInfo];

          const namesFromEvent = [fileInfo.name].filter(Boolean);
          this.uploadedNames = Array.from(
            new Set([...(this.uploadedNames || []), ...namesFromEvent])
          );
          localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));
        });
      } else {
        this.lockedPdfError = null;

        this.pendingFiles = [...this.pendingFiles, fileInfo];

        const namesFromEvent = [fileInfo.name].filter(Boolean);
        this.uploadedNames = Array.from(
          new Set([...(this.uploadedNames || []), ...namesFromEvent])
        );
        localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));
      }
    });
  }

  uploadAll(): void {
    if (!this.pendingFiles.length) {
      return;
    }

    const token = localStorage.getItem('rm_access_token') || '';
    const userDataRaw = localStorage.getItem('rm_current_user') || '{}';
    const userData = JSON.parse(userDataRaw);
    const pcnNumber = this.global.PCN.pcn;
    const contravention = this.global.PCN.contravention;

    const form = new FormData();

    for (const file of this.pendingFiles) {
      if (file.rawFile) {
        form.append('files', file.rawFile, file.name);
      }
    }

    form.append('pcnNumber', pcnNumber);
    form.append('userId', userData?.userId ?? '');
    form.append('username', userData?.username ?? '');
    form.append('contravention', contravention);
    form.append('sessionGuid', this.ensureSession());

    let headers = new HttpHeaders().set('Accept', 'application/json');
    if (token) {
      headers = headers.set('Authorization', `Bearer ${token}`);
    }

    if (!this.batchActive) {
      this.batchActive = true;
      this.isSubmitting = true;
      this.summaryText = null;
      this.isWaitingForSummary = true;
      this.startFakeProgress();
      this.storageService.lockLetterTop(true);
    }

    this.http
      .post<NexusUploadResponse>(this.uploadSaveUrl, form, { headers })
      .pipe(takeUntil(this.cancelNexusRequest$))
      .subscribe({
        next: (response) => {
          const reasonIds = response?.reasonId ?? [];
          const challengeText = response?.challengeText ?? '';
          const sessionId = response?.sessionGuid ?? '';

          this.summaryText = response?.summary || 'No summary available.';
          this.evidenceText = response?.evidence || 'No evidence summary available.';
          this.evidenceDocuments = (response?.evidenceDocuments as unknown as string) ?? null;
          localStorage.setItem(
            this.LS_KEYS.evidenceDocGuesses,
            JSON.stringify(response?.evidenceDocumentGuesses ?? [])
          );
          localStorage.setItem(
            this.LS_KEYS.evidenceDocConfidences,
            JSON.stringify(response?.evidenceDocumentConfidences ?? [])
          );

          localStorage.setItem(this.LS_KEYS.summary, this.summaryText ?? '');
          localStorage.setItem(this.LS_KEYS.evidence, this.evidenceText ?? '');
          localStorage.setItem(
            this.LS_KEYS.evidenceDocs,
            JSON.stringify(this.evidenceDocuments || [])
          );
          localStorage.setItem(this.LS_KEYS.sessionGuid, sessionId);

          const pcn = this.global?.PCN?.pcn ?? 'unknown';
          const meta: UploadedMeta = {
            name:
              response?.fileName ||
              this.pendingFiles
                .map((f) => f.name)
                .filter(Boolean)
                .join(', ') ||
              'uploaded.pdf',
            size:
              response?.fileSize ||
              this.pendingFiles.reduce(
                (sum, f) => sum + (f.rawFile ? f.rawFile.size : f.size || 0),
                0
              ),
            sessionGuid: sessionId,
            pcn,
          };
          localStorage.setItem(this.getMetaKey(), JSON.stringify(meta));
          this.uploadedMeta = meta;
          this.isFileUploaded = true;

          this.uploadedFilesCount += this.pendingFiles.length;

          const namesFromBatch = this.pendingFiles.map((f) => f.name).filter(Boolean);
          this.uploadedNames = Array.from(
            new Set([...(this.uploadedNames || []), ...namesFromBatch])
          );
          localStorage.setItem(this.getNamesKey(), JSON.stringify(this.uploadedNames));
          this.refreshUploadedMetaName();

          const uploadedInfos: FileInfo[] = this.pendingFiles.map((f) => ({
            name: f.name,
            size: f.size ?? (f.rawFile ? f.rawFile.size : 0),
            extension: '.' + (f.name.split('.').pop() || '').toLowerCase(),
            state: FileState.Uploaded,
            uid: this.makeUid(),
          }));

          this.persistedFiles = [...(this.persistedFiles || []), ...uploadedInfos];

          this.storageService.lockLetterTop(false);
          this.isWaitingForSummary = false;
          this.stopFakeProgress();
          this.batchActive = false;
          this.isSubmitting = false;

          this.handleReasonAndFetchQuestions(reasonIds, challengeText, sessionId);
        },
        error: () => {
          this.stopFakeProgress();
          this.isWaitingForSummary = false;
          this.isSubmitting = false;
          this.batchActive = false;
          this.storageService.lockLetterTop(false);
        },
      });
  }

  clearAll(): void {
    if (this.upload) {
      this.upload.clearFiles();
    }
    this.pendingFiles = [];
  }

  private postSessionAudit(
    sessionGuid: string,
    headers: HttpHeaders,
    selectedDocuments: any[]
  ): void {
    let params = new HttpParams().set('sessionGuid', sessionGuid);
    for (const reason of this.global.SELECTED_REASONS) {
      params = params.append('updatedReasons', reason.id.toString());
    }
    for (const doc of selectedDocuments || []) {
      if (doc?.caption) {
        params = params.append('updatedDocuments', doc.caption);
      }
    }

    // Only questions the user touched: an untouched question may hold an AI default, and reporting
    // it here would record that default as human-confirmed in NexusAudit.UserQuestionIds. An empty
    // list leaves the column as the incremental posts left it.
    const body = {
      questionsAndAnswers: filterTouchedQuestions(
        this.global.questionsAndAnswers,
        this.global.touchedQuestionIds
      ),
      additionalExplanationIds: [] as number[],
    };

    const url = `${environment.apiPath}api/nexus/updateSessionAudit`;
    this.http.post(url, body, { headers, params }).subscribe();
  }

  private isPdfLocked(raw: File): Promise<boolean> {
    return new Promise((resolve) => {
      const reader = new FileReader();
      const chunk = raw.slice(0, 1024 * 1024);

      reader.onerror = () => resolve(false);

      reader.onload = () => {
        try {
          const bytes = new Uint8Array(reader.result as ArrayBuffer);
          const text = new TextDecoder('ascii').decode(bytes);
          resolve(text.includes('/Encrypt'));
        } catch {
          resolve(false);
        }
      };

      reader.readAsArrayBuffer(chunk);
    });
  }

  private getMetaKey(): string {
    const pcn = this.global?.PCN?.pcn ?? 'unknown';
    return `nexus_uploaded_meta_${pcn}`;
  }

  private getNamesKey(): string {
    const pcn = this.global?.PCN?.pcn ?? 'unknown';
    return `nexus_uploaded_names_${pcn}`;
  }

  private makeUid(): string {
    return Math.random().toString(36).slice(2) + Date.now().toString(36);
  }

  private refreshUploadedMetaName(): void {
    const joined = Array.from(new Set(this.uploadedNames)).filter(Boolean).join(', ');
    if (!this.uploadedMeta) {
      this.uploadedMeta = {
        name: joined,
        size: 0,
        sessionGuid: localStorage.getItem(this.LS_KEYS.sessionGuid) || '',
        pcn: this.global?.PCN?.pcn ?? 'unknown',
      };
    } else {
      this.uploadedMeta.name = joined || this.uploadedMeta.name || '';
    }
  }
}
